import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import CapturesGallery from '../CapturesGallery';
import TooltipHost from '../charts/Tooltip';
import ExportModal from './ExportModal';
import { SlideContent } from './SynthPresentation';
import {
  CustomTheme,
  FontId,
  FONT_CHOICES,
  Orientation,
  ThemeId,
  getFont,
  normalizeThemeId,
  themeClass,
} from './themes';
import {
  EditorMode,
  Section as SharedSection,
  SectionType as SharedSectionType,
  autoAdjustSpans,
  inferDefaultWidth,
  isValidWidth,
} from './shared/layout';
import {
  AutoSaveBadge,
  PresentationCanvas,
  PresentationWrap,
} from './shared/PresentationCanvas';
import { ModePicker, OrientationPicker, ThemeSwatches } from './shared/Pickers';
import { useAutoSave } from './shared/useAutoSave';
import { exportHtmlStandalone as sharedExportHtml } from './shared/exportHtml';
import { useSyntheseTheme } from './shared/SyntheseContext';
import {
  IconBack,
  IconDownload,
  IconPlay,
  IconPlus,
  IconSave,
  IconTrash,
} from './shared/icons';

// ============================================================
// COMPOSITION — éditeur manuel de présentation.
// Utilise le même canvas/DnD/thèmes/orientation que Présentation
// (composants dans shared/). Pas de génération IA : l'utilisateur
// part d'une toile blanche et ajoute des blocs.
// ============================================================

type Section = SharedSection;
type SectionType = SharedSectionType;

type Composition = {
  title: string;
  sections: Section[];
  /** Orientation indépendante de celle de Présentation. */
  orientation?: Orientation;
  /** Custom theme (masque PDF) si le thème courant = 'custom'. */
  custom?: CustomTheme;
};

type SavedRow = {
  id: number;
  title: string;
  kind: string;
  is_template: number;
  created_at: string;
  updated_at: string;
  author: string | null;
};

const DEFAULT_COMPOSITION: Composition = {
  title: 'Nouvelle composition',
  sections: [],
  orientation: 'portrait',
};

function genId(): string {
  return 'b' + Math.random().toString(36).slice(2, 10);
}

// Templates de blocs lorsque l'utilisateur clique "+ Bloc X".
const BLOCK_TEMPLATES: Record<SectionType, () => Section> = {
  hero: () => ({
    id: genId(),
    type: 'hero',
    width: 12,
    title: 'Titre de la présentation',
    body: 'Sous-titre ou contexte du projet',
  }),
  text: () => ({
    id: genId(),
    type: 'text',
    width: 6,
    title: 'Titre de la section',
    body: 'Corps du texte. Supporte **gras**, *italique*, listes à puces via - ou *.',
  }),
  kpi: () => ({
    id: genId(),
    type: 'kpi',
    width: 12,
    title: 'Indicateurs clés',
    kpis: [
      { label: 'Budget', value: '6 090 950 CHF' },
      { label: 'Surface', value: '517,5 m²' },
      { label: 'Délai', value: '18 mois' },
    ],
  }),
  chart: () => ({
    id: genId(),
    type: 'chart',
    width: 6,
    title: 'Nouveau graphique',
    chart: {
      kind: 'bar',
      data: [
        { label: 'A', value: 30 },
        { label: 'B', value: 55 },
        { label: 'C', value: 42 },
      ],
      xKey: 'label',
      yKey: 'value',
      label: 'Valeur',
    },
  }),
  table: () => ({
    id: genId(),
    type: 'table',
    width: 12,
    title: 'Tableau',
    table: {
      columns: ['Poste', 'Valeur', 'Unité'],
      rows: [
        ['Ligne 1', '100', 'm²'],
        ['Ligne 2', '200', 'm²'],
      ],
    },
  }),
  timeline: () => ({
    id: genId(),
    type: 'timeline',
    width: 12,
    title: 'Planning',
    timeline: {
      items: [
        { date: '2026-01', title: 'Démarrage', text: 'Lancement du projet' },
        { date: '2026-06', title: 'Jalon mi-parcours' },
      ],
    },
  }),
  quote: () => ({
    id: genId(),
    type: 'quote',
    width: 6,
    quote: 'Citation ou exergue à mettre en avant.',
  }),
  capture: () => ({
    id: genId(),
    type: 'capture',
    width: 6,
    title: 'Capture 3D',
    capture_id: 0,
  }),
};

/** Normalise un payload chargé depuis D1 (sections, widths, orientation, custom). */
function normalizeComposition(p: any, titleFallback: string): Composition {
  const sections: Section[] = Array.isArray(p?.sections)
    ? p.sections.map((s: any, i: number) => ({
        id: typeof s?.id === 'string' ? s.id : `s${i}`,
        type: s?.type ?? 'text',
        ...(s ?? {}),
        width: isValidWidth(s?.width) ? s.width : inferDefaultWidth(s),
      }))
    : [];
  const custom = p?.custom && typeof p.custom === 'object' ? (p.custom as CustomTheme) : undefined;
  const orientation: Orientation =
    custom?.mask_orientation ?? (p?.orientation === 'landscape' ? 'landscape' : 'portrait');
  return {
    title: typeof p?.title === 'string' ? p.title : titleFallback,
    sections,
    orientation,
    custom,
  };
}

export default function SynthComposition({ projectId }: { projectId: number }) {
  // Thème partagé avec Présentation via le context Synthèse.
  const { theme: sharedTheme, setTheme: setSharedTheme } = useSyntheseTheme();
  const [composition, setComposition] = useState<Composition>(DEFAULT_COMPOSITION);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [savedList, setSavedList] = useState<SavedRow[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [showList, setShowList] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState<number | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('edition');
  const exportRef = useRef<HTMLDivElement>(null);
  // Refs pour auto-save (closure stable malgré les re-renders).
  const compositionRef = useRef<Composition>(composition);
  const currentIdRef = useRef<number | null>(null);
  useEffect(() => { compositionRef.current = composition; }, [composition]);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);

  // effectiveTheme = le thème du contexte (partagé entre Présentation et Composition).
  const effectiveTheme: ThemeId = normalizeThemeId(sharedTheme);
  const customStyle: React.CSSProperties | undefined =
    effectiveTheme === 'custom'
      ? ({
          '--custom-accent': composition.custom?.accent ?? '#3D5A45',
          '--custom-font': getFont(composition.custom?.font),
        } as React.CSSProperties)
      : undefined;

  async function refreshSaved() {
    setSavedLoading(true);
    try {
      const r = await api.get<{ syntheses: SavedRow[] }>(`/projects/${projectId}/synthesis`);
      const list = Array.isArray(r?.syntheses) ? r.syntheses : [];
      setSavedList(list.filter((s) => s.kind === 'composition'));
    } catch {
      setSavedList([]);
    } finally {
      setSavedLoading(false);
    }
  }
  useEffect(() => {
    refreshSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-save debounced 1500 ms (drop DnD, resize, changement d'orientation).
  const { status: saveStatus, trigger: scheduleAutoSave } = useAutoSave(async () => {
    const c = compositionRef.current;
    if (!c) return;
    const r = await api.post<{ id: number }>(`/projects/${projectId}/synthesis`, {
      id: currentIdRef.current ?? undefined,
      kind: 'composition',
      title: c.title,
      payload: c,
    });
    if (currentIdRef.current !== r.id) setCurrentId(r.id);
    refreshSaved();
  });

  async function loadSaved(id: number) {
    try {
      const r = await api.get<{ synthesis: { id: number; title: string; payload: any } }>(
        `/synthesis/${id}`
      );
      const comp = normalizeComposition(r.synthesis.payload ?? {}, r.synthesis.title);
      // Un ancien payload peut avoir stocké le thème ; on pousse dans le context partagé.
      if (r.synthesis.payload?.theme) setSharedTheme(normalizeThemeId(r.synthesis.payload.theme));
      setComposition(comp);
      setCurrentId(r.synthesis.id);
      setSelectedIdx(null);
      setShowList(false);
    } catch (e: any) {
      alert(`Chargement impossible : ${e?.message ?? e}`);
    }
  }

  async function deleteSaved(id: number) {
    if (!confirm('Supprimer cette composition ?')) return;
    try {
      await api.del(`/synthesis/${id}`);
      if (currentId === id) {
        setCurrentId(null);
        setComposition(DEFAULT_COMPOSITION);
      }
      refreshSaved();
    } catch (e: any) {
      alert(`Suppression impossible : ${e?.message ?? e}`);
    }
  }

  function newComposition() {
    setComposition(DEFAULT_COMPOSITION);
    setCurrentId(null);
    setSelectedIdx(null);
    setShowList(false);
  }

  async function save() {
    const title = prompt('Titre de la composition', composition.title) ?? composition.title;
    try {
      const r = await api.post<{ id: number }>(`/projects/${projectId}/synthesis`, {
        id: currentId ?? undefined,
        kind: 'composition',
        title,
        payload: { ...composition, title },
      });
      setCurrentId(r.id);
      setComposition({ ...composition, title });
      refreshSaved();
    } catch (e: any) {
      alert(`Sauvegarde impossible : ${e?.message ?? e}`);
    }
  }

  function addSection(type: SectionType) {
    const sec = BLOCK_TEMPLATES[type]();
    setComposition((c) => ({ ...c, sections: autoAdjustSpans([...c.sections, sec]) }));
    setSelectedIdx(composition.sections.length);
    scheduleAutoSave();
  }

  function updateSection(idx: number, patch: Partial<Section>) {
    setComposition((c) => {
      const next = { ...c, sections: [...c.sections] };
      next.sections[idx] = { ...next.sections[idx], ...patch };
      return next;
    });
    scheduleAutoSave();
  }

  function removeSection(idx: number) {
    setComposition((c) => ({
      ...c,
      sections: autoAdjustSpans(c.sections.filter((_, i) => i !== idx)),
    }));
    if (selectedIdx === idx) setSelectedIdx(null);
    else if (selectedIdx !== null && selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
    scheduleAutoSave();
  }

  function moveSection(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= composition.sections.length) return;
    setComposition((c) => {
      const next = { ...c, sections: [...c.sections] };
      [next.sections[idx], next.sections[j]] = [next.sections[j], next.sections[idx]];
      return { ...next, sections: autoAdjustSpans(next.sections) };
    });
    if (selectedIdx === idx) setSelectedIdx(j);
    else if (selectedIdx === j) setSelectedIdx(idx);
    scheduleAutoSave();
  }

  const selectedSection = selectedIdx !== null ? composition.sections[selectedIdx] : null;

  return (
    <>
      <TooltipHost />

      {/* ============== Liste des compositions sauvegardées ============== */}
      {showList && (savedList.length > 0 || savedLoading) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>Mes compositions sauvegardées</strong>
            <span className="card-meta" style={{ fontSize: 11 }}>
              {savedList.length} enregistrée{savedList.length > 1 ? 's' : ''}
            </span>
            <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={newComposition}>
              <IconPlus /> Nouvelle composition
            </button>
          </div>
          <div className="saved-grid">
            {savedList.map((s) => (
              <div key={s.id} className="saved-card">
                <div className="saved-card-body" onClick={() => loadSaved(s.id)}>
                  <div className="saved-title">{s.title}</div>
                  <div className="saved-meta">
                    {new Date(s.updated_at.replace(' ', 'T') + 'Z').toLocaleDateString('fr-CH')}
                    {s.author ? ` · ${s.author}` : ''}
                  </div>
                </div>
                <div className="saved-actions">
                  <button
                    className="btn btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    onClick={() => loadSaved(s.id)}
                  >
                    Ouvrir
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ padding: '2px 8px', fontSize: 11, color: '#b84b2a' }}
                    onClick={() => deleteSaved(s.id)}
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showList && savedList.length === 0 && !savedLoading && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <h3 style={{ margin: 0 }}>Composition manuelle</h3>
          <p style={{ color: 'var(--c-muted)', fontSize: 13, maxWidth: 520, margin: '12px auto' }}>
            Construis une présentation bloc par bloc : texte, KPI, graphique, tableau, timeline,
            capture 3D. Pas de génération IA — c'est toi qui pilotes le contenu.
          </p>
          <button className="btn btn-primary" onClick={newComposition}>
            <IconPlus /> Commencer une composition
          </button>
        </div>
      )}

      {/* ============== Éditeur ============== */}
      {!showList && (
        <>
          {/* --- Toolbar principal --- */}
          <div
            className="card pres-toolbar"
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <input
              type="text"
              value={composition.title}
              onChange={(e) => setComposition((c) => ({ ...c, title: e.target.value }))}
              style={{
                flex: '1 1 240px',
                minWidth: 200,
                padding: '6px 10px',
                border: '1px solid var(--c-border)',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                background: 'var(--c-bg)',
              }}
              placeholder="Titre de la composition"
            />
            <AutoSaveBadge status={saveStatus} />

            <ThemeSwatches value={effectiveTheme} onChange={(t) => setSharedTheme(t)} />

            <OrientationPicker
              value={composition.orientation ?? 'portrait'}
              locked={
                effectiveTheme === 'custom' && !!composition.custom?.mask_orientation
                  ? composition.custom!.mask_orientation!
                  : null
              }
              onChange={(o) => {
                setComposition((c) => ({ ...c, orientation: o }));
                scheduleAutoSave();
              }}
            />

            <ModePicker value={editorMode} onChange={setEditorMode} />

            {effectiveTheme === 'custom' && (
              <button
                className="btn btn-ghost slide-tool-btn"
                onClick={() => setCustomOpen(true)}
                title="Configurer le thème custom (masque PDF, police, couleur)"
              >
                Config
              </button>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowList(true)}>
                <IconBack /> Liste
              </button>
              <button className="btn btn-ghost" onClick={save}>
                <IconSave /> {currentId ? 'Mettre à jour' : 'Sauvegarder'}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setFullscreen(0)}
                disabled={composition.sections.length === 0}
              >
                <IconPlay /> Présenter
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setExportOpen(true)}
                disabled={composition.sections.length === 0}
              >
                <IconDownload /> Exporter
              </button>
            </div>
          </div>

          {/* --- Toolbar d'ajout de blocs --- */}
          <div
            className="card"
            style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: 10 }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>Ajouter un bloc :</span>
            {[
              { t: 'hero' as SectionType, lbl: 'Titre' },
              { t: 'text' as SectionType, lbl: 'Texte' },
              { t: 'kpi' as SectionType, lbl: 'KPIs' },
              { t: 'chart' as SectionType, lbl: 'Graphique' },
              { t: 'table' as SectionType, lbl: 'Tableau' },
              { t: 'timeline' as SectionType, lbl: 'Timeline' },
              { t: 'quote' as SectionType, lbl: 'Citation' },
              { t: 'capture' as SectionType, lbl: 'Capture 3D' },
            ].map(({ t, lbl }) => (
              <button
                key={t}
                className="btn btn-ghost"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => addSection(t)}
              >
                <IconPlus /> {lbl}
              </button>
            ))}
          </div>

          {/* --- Canvas + inspecteur --- */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: selectedSection ? '1fr 360px' : '1fr',
              gap: 14,
            }}
          >
            <PresentationWrap
              innerRef={exportRef}
              themeClassName={themeClass(effectiveTheme)}
              orientation={composition.orientation ?? 'portrait'}
              customPreview={effectiveTheme === 'custom' && !!composition.custom?.mask_key}
              customStyle={customStyle}
            >
              {composition.sections.length === 0 ? (
                <div
                  style={{
                    width: '100%',
                    padding: 40,
                    textAlign: 'center',
                    color: 'var(--c-muted)',
                    border: '2px dashed var(--c-border)',
                    borderRadius: 14,
                    fontSize: 13,
                  }}
                >
                  Canevas vide. Utilise la barre ci-dessus pour ajouter un premier bloc.
                </div>
              ) : (
                <PresentationCanvas
                  sections={composition.sections}
                  mode={editorMode}
                  selectedIdx={selectedIdx}
                  onSelect={(i) => setSelectedIdx(i)}
                  renderContent={({ section, onEdit }) => (
                    <SlideContent section={section as any} onEdit={onEdit} />
                  )}
                  onReorder={(next) => {
                    setComposition((c) => ({ ...c, sections: next }));
                    scheduleAutoSave();
                  }}
                  onMoveUp={(i) => moveSection(i, -1)}
                  onMoveDown={(i) => moveSection(i, 1)}
                  onEdit={(i, patch) => updateSection(i, patch)}
                  onDelete={(i) => removeSection(i)}
                  onWidthChange={(i, w) => updateSection(i, { width: w })}
                />
              )}
            </PresentationWrap>

            {selectedSection && selectedIdx !== null && (
              <BlockInspector
                section={selectedSection}
                projectId={projectId}
                onChange={(patch) => updateSection(selectedIdx, patch)}
                onClose={() => setSelectedIdx(null)}
              />
            )}
          </div>
        </>
      )}

      {exportOpen && (
        <ExportModal
          projectId={projectId}
          title={composition.title}
          kind="composition"
          payload={{ presentation: { ...composition, theme: effectiveTheme } }}
          onClose={() => setExportOpen(false)}
          onHtml={async (opts) => {
            if (!exportRef.current) return;
            await sharedExportHtml({
              exportEl: exportRef.current,
              title: composition.title || 'Composition',
              projectId,
              opts,
            });
            setExportOpen(false);
          }}
        />
      )}

      {fullscreen !== null && (
        <FullscreenComp
          composition={composition}
          theme={effectiveTheme}
          start={fullscreen}
          onClose={() => setFullscreen(null)}
        />
      )}

      {customOpen && (
        <CustomThemeDrawer
          projectId={projectId}
          current={composition.custom ?? {}}
          onClose={() => setCustomOpen(false)}
          onChange={(next) => {
            setComposition((c) => ({
              ...c,
              custom: next,
              orientation: next.mask_orientation ?? c.orientation ?? 'portrait',
            }));
            scheduleAutoSave();
          }}
        />
      )}
    </>
  );
}

// ============================================================
// Fullscreen — lecture séquentielle de la composition.
// ============================================================

function FullscreenComp({
  composition,
  theme,
  start,
  onClose,
}: {
  composition: Composition;
  theme: ThemeId;
  start: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(start);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ')
        setIdx((v) => Math.min(v + 1, composition.sections.length - 1));
      if (e.key === 'ArrowLeft') setIdx((v) => Math.max(v - 1, 0));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [composition.sections.length, onClose]);
  const section = composition.sections[idx];
  if (!section) return null;
  return (
    <div className="present-fullscreen">
      <div className={`fs-slide pres-theme-wrap ${themeClass(theme)}`} key={idx} style={{ padding: 0 }}>
        <div
          className={`presentation-slide ${section.type === 'hero' ? 'slide-hero' : ''}`}
          data-section-idx={String(idx + 1).padStart(2, '0')}
        >
          <SlideContent section={section as any} fullscreen />
        </div>
      </div>
      <div className="fs-nav">
        <button onClick={() => setIdx((v) => Math.max(0, v - 1))}>←</button>
        {composition.sections.map((_, i) => (
          <div
            key={i}
            className={`fs-dot ${idx === i ? 'active' : ''}`}
            onClick={() => setIdx(i)}
            style={{ cursor: 'pointer' }}
          />
        ))}
        <button onClick={() => setIdx((v) => Math.min(composition.sections.length - 1, v + 1))}>→</button>
        <button onClick={onClose}>×</button>
      </div>
    </div>
  );
}

// ============================================================
// BlockInspector — formulaires d'édition par type de bloc.
// ============================================================

function BlockInspector({
  section,
  projectId,
  onChange,
  onClose,
}: {
  section: Section;
  projectId: number;
  onChange: (patch: Partial<Section>) => void;
  onClose: () => void;
}) {
  return (
    <aside
      className="card"
      style={{ position: 'sticky', top: 12, maxHeight: 'calc(100vh - 40px)', overflow: 'auto' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <strong style={{ fontSize: 13 }}>Édition — {labelOfType(section.type)}</strong>
        <button
          className="btn btn-ghost"
          style={{ marginLeft: 'auto', padding: '2px 8px' }}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {section.type !== 'quote' && (
        <Field label="Titre">
          <input
            type="text"
            value={section.title ?? ''}
            onChange={(e) => onChange({ title: e.target.value })}
            className="inspector-input"
          />
        </Field>
      )}

      {(section.type === 'hero' || section.type === 'text') && (
        <Field label="Corps (markdown simple)">
          <textarea
            value={section.body ?? ''}
            rows={6}
            onChange={(e) => onChange({ body: e.target.value })}
            className="inspector-input"
          />
        </Field>
      )}

      {section.type === 'quote' && (
        <Field label="Citation">
          <textarea
            value={section.quote ?? ''}
            rows={4}
            onChange={(e) => onChange({ quote: e.target.value })}
            className="inspector-input"
          />
        </Field>
      )}

      {section.type === 'kpi' && (
        <KpiEditor kpis={section.kpis ?? []} onChange={(kpis) => onChange({ kpis })} />
      )}

      {section.type === 'table' && section.table && (
        <TableEditor table={section.table} onChange={(table) => onChange({ table })} />
      )}

      {section.type === 'timeline' && section.timeline && (
        <TimelineEditor
          items={section.timeline.items}
          onChange={(items) => onChange({ timeline: { items } })}
        />
      )}

      {section.type === 'chart' && section.chart && (
        <ChartEditor chart={section.chart} onChange={(chart) => onChange({ chart })} />
      )}

      {section.type === 'capture' && (
        <CaptureEditor
          captureId={section.capture_id ?? 0}
          projectId={projectId}
          onChange={(id) => onChange({ capture_id: id })}
        />
      )}
    </aside>
  );
}

function labelOfType(t: SectionType): string {
  return {
    hero: 'Titre',
    text: 'Texte',
    kpi: 'KPIs',
    chart: 'Graphique',
    table: 'Tableau',
    timeline: 'Timeline',
    quote: 'Citation',
    capture: 'Capture 3D',
  }[t];
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-muted)', display: 'block', marginBottom: 4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

// --- KPI ---
function KpiEditor({
  kpis,
  onChange,
}: {
  kpis: Array<{ label: string; value: string; trend?: string }>;
  onChange: (k: Array<{ label: string; value: string; trend?: string }>) => void;
}) {
  const update = (i: number, patch: Partial<{ label: string; value: string; trend?: string }>) =>
    onChange(kpis.map((k, j) => (i === j ? { ...k, ...patch } : k)));
  const remove = (i: number) => onChange(kpis.filter((_, j) => j !== i));
  const add = () => onChange([...kpis, { label: 'Nouveau KPI', value: '0' }]);
  return (
    <div>
      {kpis.map((k, i) => (
        <div key={i} style={{ background: 'var(--c-nav)', padding: 8, borderRadius: 8, marginBottom: 6 }}>
          <input
            type="text"
            value={k.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Label"
            className="inspector-input"
            style={{ marginBottom: 4 }}
          />
          <input
            type="text"
            value={k.value}
            onChange={(e) => update(i, { value: e.target.value })}
            placeholder="Valeur (ex: 6 090 950 CHF)"
            className="inspector-input"
            style={{ marginBottom: 4 }}
          />
          <input
            type="text"
            value={k.trend ?? ''}
            onChange={(e) => update(i, { trend: e.target.value })}
            placeholder="Tendance (optionnel, ex: +12 %)"
            className="inspector-input"
          />
          <button
            className="btn btn-ghost"
            style={{ fontSize: 10, marginTop: 4, color: '#b84b2a' }}
            onClick={() => remove(i)}
          >
            Supprimer ce KPI
          </button>
        </div>
      ))}
      <button className="btn btn-ghost" onClick={add} style={{ width: '100%', fontSize: 12 }}>
        <IconPlus /> Ajouter un KPI
      </button>
    </div>
  );
}

// --- Table ---
function TableEditor({
  table,
  onChange,
}: {
  table: { columns: string[]; rows: string[][] };
  onChange: (t: { columns: string[]; rows: string[][] }) => void;
}) {
  const updateCol = (i: number, v: string) =>
    onChange({ ...table, columns: table.columns.map((c, j) => (j === i ? v : c)) });
  const addCol = () =>
    onChange({
      columns: [...table.columns, `Colonne ${table.columns.length + 1}`],
      rows: table.rows.map((r) => [...r, '']),
    });
  const removeCol = (i: number) =>
    onChange({
      columns: table.columns.filter((_, j) => j !== i),
      rows: table.rows.map((r) => r.filter((_, j) => j !== i)),
    });
  const updateCell = (ri: number, ci: number, v: string) =>
    onChange({
      ...table,
      rows: table.rows.map((r, j) => (j === ri ? r.map((c, k) => (k === ci ? v : c)) : r)),
    });
  const addRow = () => onChange({ ...table, rows: [...table.rows, table.columns.map(() => '')] });
  const removeRow = (i: number) => onChange({ ...table, rows: table.rows.filter((_, j) => j !== i) });
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-muted)', marginBottom: 6 }}>
        Colonnes
      </div>
      {table.columns.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          <input
            type="text"
            value={c}
            onChange={(e) => updateCol(i, e.target.value)}
            className="inspector-input"
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-ghost"
            style={{ fontSize: 10, padding: '2px 6px' }}
            onClick={() => removeCol(i)}
            aria-label="Supprimer colonne"
          >
            ×
          </button>
        </div>
      ))}
      <button
        className="btn btn-ghost"
        style={{ width: '100%', fontSize: 12, marginBottom: 10 }}
        onClick={addCol}
      >
        <IconPlus /> Colonne
      </button>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-muted)', marginBottom: 6 }}>
        Lignes
      </div>
      {table.rows.map((r, ri) => (
        <div key={ri} style={{ background: 'var(--c-nav)', padding: 6, borderRadius: 6, marginBottom: 4 }}>
          {r.map((cell, ci) => (
            <input
              key={ci}
              type="text"
              value={cell}
              onChange={(e) => updateCell(ri, ci, e.target.value)}
              placeholder={table.columns[ci] ?? ''}
              className="inspector-input"
              style={{ marginBottom: 2 }}
            />
          ))}
          <button
            className="btn btn-ghost"
            style={{ fontSize: 10, color: '#b84b2a' }}
            onClick={() => removeRow(ri)}
          >
            Supprimer la ligne
          </button>
        </div>
      ))}
      <button className="btn btn-ghost" style={{ width: '100%', fontSize: 12 }} onClick={addRow}>
        <IconPlus /> Ligne
      </button>
    </div>
  );
}

// --- Timeline ---
function TimelineEditor({
  items,
  onChange,
}: {
  items: Array<{ date: string; title: string; text?: string }>;
  onChange: (items: Array<{ date: string; title: string; text?: string }>) => void;
}) {
  const update = (i: number, patch: Partial<{ date: string; title: string; text?: string }>) =>
    onChange(items.map((it, j) => (i === j ? { ...it, ...patch } : it)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, { date: '2026-01', title: 'Nouveau jalon' }]);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ background: 'var(--c-nav)', padding: 8, borderRadius: 8, marginBottom: 6 }}>
          <input
            type="text"
            value={it.date}
            onChange={(e) => update(i, { date: e.target.value })}
            placeholder="Date (AAAA-MM ou texte libre)"
            className="inspector-input"
            style={{ marginBottom: 4 }}
          />
          <input
            type="text"
            value={it.title}
            onChange={(e) => update(i, { title: e.target.value })}
            placeholder="Titre du jalon"
            className="inspector-input"
            style={{ marginBottom: 4 }}
          />
          <textarea
            value={it.text ?? ''}
            rows={2}
            onChange={(e) => update(i, { text: e.target.value })}
            placeholder="Description (optionnel)"
            className="inspector-input"
          />
          <button
            className="btn btn-ghost"
            style={{ fontSize: 10, marginTop: 4, color: '#b84b2a' }}
            onClick={() => remove(i)}
          >
            Supprimer ce jalon
          </button>
        </div>
      ))}
      <button className="btn btn-ghost" onClick={add} style={{ width: '100%', fontSize: 12 }}>
        <IconPlus /> Ajouter un jalon
      </button>
    </div>
  );
}

// --- Chart ---
function ChartEditor({
  chart,
  onChange,
}: {
  chart: NonNullable<Section['chart']>;
  onChange: (c: NonNullable<Section['chart']>) => void;
}) {
  const [dataText, setDataText] = useState<string>(() => JSON.stringify(chart.data ?? [], null, 2));
  const [dataErr, setDataErr] = useState<string | null>(null);

  useEffect(() => {
    setDataText(JSON.stringify(chart.data ?? [], null, 2));
  }, [chart.data]);

  function commitData() {
    try {
      const parsed = JSON.parse(dataText);
      if (!Array.isArray(parsed)) throw new Error('Doit être un tableau [{...}, ...]');
      onChange({ ...chart, data: parsed });
      setDataErr(null);
    } catch (e: any) {
      setDataErr(String(e.message ?? e));
    }
  }

  return (
    <div>
      <Field label="Type de graphique">
        <select
          value={chart.kind}
          onChange={(e) => onChange({ ...chart, kind: e.target.value as NonNullable<Section['chart']>['kind'] })}
          className="inspector-input"
        >
          <option value="bar">Barres</option>
          <option value="donut">Camembert</option>
          <option value="line">Courbe</option>
          <option value="radar">Radar</option>
          <option value="sankey">Flux Sankey</option>
        </select>
      </Field>

      <Field label="Unité (ex: CHF, m², %)">
        <input
          type="text"
          value={chart.unit ?? ''}
          onChange={(e) => onChange({ ...chart, unit: e.target.value })}
          className="inspector-input"
        />
      </Field>

      {chart.kind === 'bar' && (
        <Field label="Options">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={!!chart.stacked}
              onChange={(e) => onChange({ ...chart, stacked: e.target.checked })}
            />
            Empilé (stacked)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={!!chart.horizontal}
              onChange={(e) => onChange({ ...chart, horizontal: e.target.checked })}
            />
            Horizontal
          </label>
        </Field>
      )}

      <Field label="Données (JSON)">
        <textarea
          value={dataText}
          onChange={(e) => setDataText(e.target.value)}
          onBlur={commitData}
          rows={10}
          spellCheck={false}
          className="inspector-input"
          style={{ fontFamily: 'Consolas, monospace', fontSize: 11 }}
        />
        {dataErr && <div style={{ color: '#b84b2a', fontSize: 11, marginTop: 4 }}>{dataErr}</div>}
        <div style={{ fontSize: 10, color: 'var(--c-muted)', marginTop: 4 }}>
          Exemple : <code>[{"{label:'A',value:10}"}, {"{label:'B',value:20}"}]</code>
        </div>
      </Field>
    </div>
  );
}

// --- Capture ---
function CaptureEditor({
  captureId,
  projectId,
  onChange,
}: {
  captureId: number;
  projectId: number;
  onChange: (id: number) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div>
      <Field label="ID de la capture">
        <input
          type="number"
          value={captureId || ''}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="inspector-input"
        />
      </Field>
      <button
        className="btn btn-ghost"
        style={{ fontSize: 12, padding: '4px 10px' }}
        onClick={() => setShowPicker((v) => !v)}
      >
        {showPicker ? 'Masquer' : 'Parcourir la galerie'}
      </button>
      {showPicker && (
        <div style={{ marginTop: 8 }}>
          <CapturesGallery
            projectId={projectId}
            selectable
            selectedIds={new Set([captureId])}
            onToggleSelect={(id) => {
              onChange(id);
              setShowPicker(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// CustomThemeDrawer — mêmes capacités que côté Présentation.
// ============================================================

function CustomThemeDrawer({
  projectId,
  current,
  onClose,
  onChange,
}: {
  projectId: number;
  current: CustomTheme;
  onClose: () => void;
  onChange: (next: CustomTheme) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [accent, setAccent] = useState<string>(current.accent ?? '#3D5A45');
  const [font, setFont] = useState<FontId>(current.font ?? 'system');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setErr('Le masque doit être un PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr('Masque PDF trop lourd (>10 Mo).');
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      const { pages, orientation } = await detectPdfMetadata(file);
      const maskPages: 1 | 2 = pages >= 2 ? 2 : 1;
      const form = new FormData();
      form.append('file', file);
      form.append('pages', String(maskPages));
      form.append('orientation', orientation);
      const res = await fetch(
        `/api/v1/projects/${projectId}/synthesis/custom-theme-mask`,
        { method: 'POST', credentials: 'include', body: form }
      );
      if (!res.ok) throw new Error(await res.text());
      const j = (await res.json()) as { mask_key: string };
      onChange({
        ...current,
        mask_key: j.mask_key,
        mask_pages: maskPages,
        mask_orientation: orientation,
        font,
        accent,
      });
    } catch (ex: any) {
      setErr(String(ex?.message ?? ex));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="export-modal" onClick={onClose}>
      <div className="export-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <strong>Thème custom</strong>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600 }}>Masque PDF (1 ou 2 pages)</label>
        <div className="card-meta" style={{ fontSize: 11, marginBottom: 6 }}>
          1 page : appliquée à toutes les pages. 2 pages : page 1 = garde, page 2 = intérieur.
          L'orientation est détectée et verrouillée.
        </div>
        <input type="file" accept="application/pdf" onChange={handleFile} disabled={uploading} />
        {current.mask_key && (
          <div className="card-meta" style={{ fontSize: 11, marginTop: 6 }}>
            Masque actuel : {current.mask_pages ?? '?'} page(s), {current.mask_orientation ?? '?'}.
          </div>
        )}

        <div style={{ height: 16 }} />
        <label style={{ fontSize: 12, fontWeight: 600 }}>Police principale</label>
        <select
          value={font}
          onChange={(e) => {
            const next = e.target.value as FontId;
            setFont(next);
            onChange({ ...current, font: next, accent });
          }}
          style={{ width: '100%', padding: 6, marginTop: 4 }}
        >
          {FONT_CHOICES.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>

        <div style={{ height: 16 }} />
        <label style={{ fontSize: 12, fontWeight: 600 }}>Couleur d'accent</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
          <input
            type="color"
            value={accent}
            onChange={(e) => {
              setAccent(e.target.value);
              onChange({ ...current, accent: e.target.value, font });
            }}
            style={{ width: 48, height: 32, padding: 0, border: '1px solid var(--c-border)', borderRadius: 6 }}
          />
          <code style={{ fontSize: 12 }}>{accent}</code>
        </div>

        {err && <div className="error-msg" style={{ marginTop: 14 }}>{err}</div>}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

/** Détecte nb pages + orientation (via ratio largeur/hauteur de la 1re page) avec pdfjs-dist. */
async function detectPdfMetadata(file: File): Promise<{ pages: number; orientation: Orientation }> {
  const pdfjs: any = await import('pdfjs-dist');
  try {
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
    }
  } catch { /* le worker peut déjà être configuré ailleurs dans l'app */ }
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pages = doc.numPages || 1;
  const page = await doc.getPage(1);
  const vp = page.getViewport({ scale: 1 });
  const orientation: Orientation = vp.width > vp.height ? 'landscape' : 'portrait';
  try { await doc.destroy?.(); } catch {}
  return { pages, orientation };
}
